import React from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import styled, { css } from 'styled-components';

import { mediaQueries } from 'shared/styles';
import useStores from 'shared/hooks/use-stores';
import { useCheckout } from 'checkout/context';
import useTranslation from 'hooks/use-translation';
import { InterfaceVariants } from 'src/constants/interface-variants';
import { CheckoutVariants } from 'src/constants/checkout-variants.ts';
import { CheckoutLogo } from 'checkout/components/checkout-logo';
import { Typography } from 'src/typography';
import { VisuallyHidden } from 'shared/components/visually-hidden';
import { AccountSection } from './account-section';
import { InternalLogin } from './internal-login';

function CheckoutHeader() {
  const { t } = useTranslation();
  const { Cart, UI } = useStores();
  const { variant } = useCheckout();
  const isDefaultVariant = UI.variant === InterfaceVariants.dutchieMain;
  const isIsolatedCheckout = variant === CheckoutVariants.isolatedCheckout;
  const dispensaryId = _.get(Cart, `order.dispensary.id`, '');
  const dispensaryName = _.get(Cart, `order.dispensary.name`, '');
  const shouldHideAccountSection = isIsolatedCheckout && UI.hideLogin;

  if (UI.isMobileEcommApp) {
    return <InternalLogin />;
  }

  return (
    <Container>
      <Left>
        {isDefaultVariant && !isIsolatedCheckout && (
          <>
            <Logo onClick={() => UI.showMenu(dispensaryId)}>
              <CheckoutLogo />
              <VisuallyHidden>{t('checkout.return-to-menu', 'Return to Menu')}</VisuallyHidden>
            </Logo>

            <Separator />
          </>
        )}

        <Heading data-testid='checkout-title' tag='h1' weight='regular' size='medium' variant={UI.variant}>
          {(props) => (
            <>
              {isIsolatedCheckout && <>{dispensaryName} </>}
              {props.t('checkout.headers', 'Checkout')}
            </>
          )}
        </Heading>
      </Left>

      {!shouldHideAccountSection && (
        <Right>
          <AccountSection />
        </Right>
      )}
    </Container>
  );
}

export default observer(CheckoutHeader);

const Container = styled.div`
  align-items: center;
  display: flex;
  padding: 53px 0 32px;

  @media ${mediaQueries.largePhone} {
    padding: 52px 30px 29px;
  }
`;

const Left = styled.div`
  align-items: center;
  display: flex;
  flex: 1 0 0%;
  min-width: 0;

  @media ${mediaQueries.phone} {
    flex: 0 0 60%;
  }
`;

const Logo = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: block;
  height: 40px;
  margin: 0 18px 0 0;
  padding: 0;
  width: auto;

  @media ${mediaQueries.phone} {
    display: none;
  }
`;

const Separator = styled.div`
  border-left: 1px solid #d8d8d8;
  content: '';
  height: 47px;
  width: 1px;

  @media ${mediaQueries.phone} {
    height: 36px;
    display: none;
  }
`;

const Heading = styled(Typography.Heading)`
  color: #6b6f73;
  font-family: ${({ theme }) => theme.customized.fonts.secondary};
  margin: 0;
  padding-left: 19px;

  ${(props) =>
    props.variant !== 'default' &&
    css`
      padding-left: 0;
      border-left: none;
    `}

  @media ${mediaQueries.phone} {
    padding-left: 0px;
    ${(props) =>
      props.variant !== 'default' &&
      css`
        padding-left: 0;
      `}
  }
`;

const Right = styled.div`
  text-align: right;
  flex: 0 0 auto;
  min-width: 0;

  @media ${mediaQueries.phone} {
    flex: 0 0 40%;
  }
`;
